<template>
    <div class="withdraw-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="withdraw-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.id="elem">
                <div v-if="elem.item.id != null">{{ elem.item.id }}</div>
                <div v-else>-</div>
            </template>

            <template #item.email="elem">
                <div v-if="elem.item.for_user?.email != null">{{ elem.item.for_user.email }}</div>
                <div v-else>-</div>
            </template>

            <template #item.created_at="elem">
                <div v-if="elem.item.created_at != null">{{ useDataFormatWithTime(elem.item.created_at) }}</div>
                <div v-else>-</div>
            </template>

            <template #item.amount="elem">
                <div v-if="elem.item.amount != null" class="withdraw-table__money">{{ elem.item.amount }} PLN</div>
                <div v-else class="withdraw-table__money">{{ 0.00 }} PLN</div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import WithdrawApi from '@/api/v1/WithdrawApi';
import { toast } from 'vue3-toastify';
import { useDataFormatWithTime } from '@/hooks/DataHooks';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IWithdrawListResponse } from '@/models/WithdrawModel';
import KTextField from '@/components/atoms/KTextField.vue';

const headers = ref<ITableHeader[]>([
    {
        title: t('withdraw.withdrawId'),
        align: 'start',
        sortable: false,
        key: 'id',
        width: '',
    },
    {
        title: t('withdraw.email'),
        align: 'center',
        sortable: false,
        key: 'email',
        width: '',
    },
    {
        title: t('withdraw.withdrawDate'),
        align: 'center',
        sortable: false,
        key: 'created_at',
        width: '',
    },
    {
        title: t('withdraw.money'),
        align: 'center',
        sortable: false,
        key: 'amount',
        width: '',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IWithdrawListResponse[]>([]);
const withdrawApi = new WithdrawApi();
const withdrawList = async() => {
    loading.value = true;
    try {
        const result = await withdrawApi.withdrawMoneyList(
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('withdraw.toast.errorWithdrawList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('withdraw.toast.errorWithdrawList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    withdrawList();
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        withdrawList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.withdraw-table {
    margin-top: 20px;

    &__loading {
        opacity: 0.6;
    }

    &__money {
        color: $green-400;
        font-weight: 700;
    }
}
</style>
